import { useEffect, useState } from "react";
import { Calculator, Plus, CheckCircle2, Lock, Unlock, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useFideicomiso } from "../lib/fideicomiso";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const TABS = ["mayor", "estados_cuenta", "catalogo"];

function money(n, cur = "MXN") {
    return (n || 0).toLocaleString("es-MX", { style: "currency", currency: cur });
}

export default function Contabilidad() {
    const { selected } = useFideicomiso();
    const { user } = useAuth();
    const [tab, setTab] = useState("mayor");
    const [catalogo, setCatalogo] = useState([]);
    const [asientos, setAsientos] = useState([]);
    const [estados, setEstados] = useState([]);
    const [openAsiento, setOpenAsiento] = useState(false);
    const [openEC, setOpenEC] = useState(false);
    const [periodoEC, setPeriodoEC] = useState("");
    const [busy, setBusy] = useState(false);
    const [asientoForm, setAsientoForm] = useState({
        fecha: new Date().toISOString().slice(0, 10),
        concepto: "",
        partidas: [
            { cuenta_contable: "1102", debe: 0, haber: 0, moneda: "MXN", notas: "" },
            { cuenta_contable: "5103", debe: 0, haber: 0, moneda: "MXN", notas: "" },
        ],
    });

    const load = async () => {
        if (!selected) return;
        const [{ data: cat }, { data: a }, { data: e }] = await Promise.all([
            api.get("/cuentas-contables"),
            api.get(`/asientos?fideicomiso_id=${selected.id}`),
            api.get(`/estados-cuenta?fideicomiso_id=${selected.id}`),
        ]);
        setCatalogo(cat.catalogo || []);
        setAsientos(a);
        setEstados(e);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected?.id]);

    const totalDebe = asientoForm.partidas.reduce((s, p) => s + parseFloat(p.debe || 0), 0);
    const totalHaber = asientoForm.partidas.reduce((s, p) => s + parseFloat(p.haber || 0), 0);
    const balanceado = Math.abs(totalDebe - totalHaber) < 0.01 && totalDebe > 0;

    const addPartida = () => setAsientoForm((f) => ({
        ...f, partidas: [...f.partidas, { cuenta_contable: "1102", debe: 0, haber: 0, moneda: "MXN", notas: "" }],
    }));
    const rmPartida = (idx) => setAsientoForm((f) => ({
        ...f, partidas: f.partidas.filter((_, i) => i !== idx),
    }));
    const setPartida = (idx, k, v) => setAsientoForm((f) => ({
        ...f, partidas: f.partidas.map((p, i) => i === idx ? { ...p, [k]: v } : p),
    }));

    const submitAsiento = async (e) => {
        e.preventDefault();
        if (!balanceado) return toast.error("Asiento desbalanceado");
        setBusy(true);
        try {
            await api.post(`/asientos?fideicomiso_id=${selected.id}`, {
                fecha: asientoForm.fecha,
                concepto: asientoForm.concepto,
                origen: "manual",
                partidas: asientoForm.partidas.map((p) => ({
                    ...p, debe: parseFloat(p.debe || 0), haber: parseFloat(p.haber || 0),
                })),
            });
            toast.success("Asiento creado en borrador");
            setOpenAsiento(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const aprobar = async (id) => {
        try {
            await api.post(`/asientos/${id}/aprobar`);
            toast.success("Asiento aprobado");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    const generarEC = async () => {
        if (!periodoEC || !/^\d{4}-\d{2}$/.test(periodoEC)) return toast.error("Periodo YYYY-MM");
        setBusy(true);
        try {
            await api.post(`/estados-cuenta/generar?fideicomiso_id=${selected.id}&periodo=${periodoEC}`);
            toast.success("Estado de cuenta generado");
            setOpenEC(false); setPeriodoEC("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const cerrarEC = async (id) => {
        try {
            await api.post(`/estados-cuenta/${id}/cerrar`);
            toast.success("Periodo cerrado");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    const reabrirEC = async (id) => {
        const motivo = prompt("Motivo de la reapertura:");
        if (!motivo) return;
        try {
            await api.post(`/estados-cuenta/${id}/reabrir`, { motivo });
            toast.success("Periodo reabierto");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    };

    const descargarPDF = async (id, folio) => {
        try {
            const r = await api.get(`/estados-cuenta/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `${folio}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error("Error descargando"); }
    };

    if (!selected) return <div className="p-10 text-muted-foreground text-sm">Selecciona un fideicomiso.</div>;

    const catMap = Object.fromEntries(catalogo.map((c) => [c.codigo, c]));

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="contabilidad-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Núcleo · {selected.numero_interno}
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Calculator className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Contabilidad
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Mayor auxiliar del fideicomiso · candado por periodo cerrado
                    </p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={openAsiento} onOpenChange={setOpenAsiento}>
                        <DialogTrigger asChild>
                            <Button data-testid="nuevo-asiento-btn">
                                <Plus className="h-4 w-4 mr-1" /> Nuevo asiento
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader><DialogTitle>Nuevo asiento contable</DialogTitle></DialogHeader>
                            <form onSubmit={submitAsiento} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Fecha</Label>
                                        <Input type="date" required value={asientoForm.fecha}
                                            onChange={(e) => setAsientoForm({ ...asientoForm, fecha: e.target.value })} />
                                    </div>
                                    <div><Label>Concepto</Label>
                                        <Input required value={asientoForm.concepto}
                                            onChange={(e) => setAsientoForm({ ...asientoForm, concepto: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label>Partidas</Label>
                                        <Button type="button" size="sm" variant="ghost" onClick={addPartida}
                                            data-testid="asiento-partida-add">
                                            <Plus className="h-3 w-3 mr-1" /> Añadir
                                        </Button>
                                    </div>
                                    <table className="trf-table text-xs">
                                        <thead><tr><th>Cuenta</th><th>Debe</th><th>Haber</th><th>Notas</th><th></th></tr></thead>
                                        <tbody>
                                            {asientoForm.partidas.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <select className="w-full border border-border rounded-sm px-1 py-1 bg-background text-xs"
                                                            value={p.cuenta_contable}
                                                            onChange={(e) => setPartida(idx, "cuenta_contable", e.target.value)}>
                                                            {catalogo.map((c) => (
                                                                <option key={c.codigo} value={c.codigo}>
                                                                    {c.codigo} · {c.nombre}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td><Input type="number" step="0.01" value={p.debe} className="w-24 text-right"
                                                        onChange={(e) => setPartida(idx, "debe", e.target.value)} /></td>
                                                    <td><Input type="number" step="0.01" value={p.haber} className="w-24 text-right"
                                                        onChange={(e) => setPartida(idx, "haber", e.target.value)} /></td>
                                                    <td><Input value={p.notas} className="text-xs"
                                                        onChange={(e) => setPartida(idx, "notas", e.target.value)} /></td>
                                                    <td>
                                                        <Button type="button" size="sm" variant="ghost"
                                                            onClick={() => rmPartida(idx)}
                                                            disabled={asientoForm.partidas.length <= 2}>×</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="font-bold">
                                                <td className="text-right">Totales</td>
                                                <td className="text-right tabular">${totalDebe.toFixed(2)}</td>
                                                <td className="text-right tabular">${totalHaber.toFixed(2)}</td>
                                                <td colSpan={2}>
                                                    {balanceado
                                                        ? <span className="badge-exito">Balanceado</span>
                                                        : <span className="badge-error">Desbalance ${(totalDebe - totalHaber).toFixed(2)}</span>}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={busy || !balanceado}>
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar en borrador"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    <Dialog open={openEC} onOpenChange={setOpenEC}>
                        <DialogTrigger asChild>
                            <Button variant="outline" data-testid="generar-estado-cuenta-btn">
                                Generar estado de cuenta
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Generar estado de cuenta</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div><Label>Periodo (YYYY-MM)</Label>
                                    <Input placeholder="2026-08" value={periodoEC}
                                        onChange={(e) => setPeriodoEC(e.target.value)} />
                                </div>
                                <Button onClick={generarEC} disabled={busy}>
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generar"}
                                </Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="mayor" data-testid="tab-mayor">Mayor</TabsTrigger>
                    <TabsTrigger value="estados_cuenta" data-testid="tab-estados-cuenta">Estados de cuenta</TabsTrigger>
                    <TabsTrigger value="catalogo" data-testid="tab-catalogo">Catálogo</TabsTrigger>
                </TabsList>

                <TabsContent value="mayor">
                    <div className="rounded-md border border-border bg-card overflow-hidden">
                        <table className="trf-table">
                            <thead><tr>
                                <th>#</th><th>Fecha</th><th>Concepto</th><th>Origen</th>
                                <th className="text-right">Debe</th><th className="text-right">Haber</th>
                                <th>Estado</th><th></th>
                            </tr></thead>
                            <tbody>
                                {asientos.map((a) => {
                                    const debe = a.partidas.reduce((s, p) => s + (p.debe || 0), 0);
                                    const haber = a.partidas.reduce((s, p) => s + (p.haber || 0), 0);
                                    const puedeAprobar = a.estado === "borrador" && a.creado_por !== user?.email;
                                    return (
                                        <tr key={a.id} data-testid={`asiento-row-${a.id}`}>
                                            <td className="tabular text-xs">{a.numero_asiento}</td>
                                            <td className="tabular text-xs">{a.fecha}</td>
                                            <td className="text-xs">{a.concepto}</td>
                                            <td><span className="badge-gold text-[10px]">{a.origen}</span></td>
                                            <td className="text-right tabular">{money(debe)}</td>
                                            <td className="text-right tabular">{money(haber)}</td>
                                            <td>
                                                <span className={
                                                    a.estado === "cerrado" ? "badge-mock" :
                                                    a.estado === "aprobado" ? "badge-exito" : "badge-gold"
                                                }>{a.estado}</span>
                                            </td>
                                            <td>
                                                {puedeAprobar && (
                                                    <Button size="sm" variant="ghost"
                                                        onClick={() => aprobar(a.id)}
                                                        data-testid={`aprobar-asiento-${a.id}`}>
                                                        Aprobar
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {asientos.length === 0 && (
                                    <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">Sin asientos.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="estados_cuenta">
                    <div className="rounded-md border border-border bg-card overflow-hidden">
                        <table className="trf-table">
                            <thead><tr>
                                <th>Folio</th><th>Periodo</th><th>Generado</th>
                                <th className="text-right">Debe periodo</th>
                                <th className="text-right">Haber periodo</th>
                                <th>Estado</th><th></th>
                            </tr></thead>
                            <tbody>
                                {estados.map((e) => (
                                    <tr key={e.id} data-testid={`ec-row-${e.id}`}>
                                        <td className="tabular font-medium text-xs">{e.folio}</td>
                                        <td className="tabular">{e.periodo}</td>
                                        <td className="tabular text-[10px]">{e.fecha_generacion?.substring(0, 10)}</td>
                                        <td className="text-right tabular">{money(e.totales_periodo?.debe)}</td>
                                        <td className="text-right tabular">{money(e.totales_periodo?.haber)}</td>
                                        <td>
                                            <span className={e.estado === "cerrado" ? "badge-mock" : "badge-exito"}>
                                                {e.estado === "cerrado" ? <><Lock className="h-3 w-3 inline mr-1" />cerrado</> : e.estado}
                                            </span>
                                        </td>
                                        <td className="flex gap-1">
                                            <Button size="sm" variant="ghost"
                                                onClick={() => descargarPDF(e.id, e.folio)}
                                                data-testid={`descargar-ec-${e.id}`}>
                                                <FileDown className="h-3 w-3" />
                                            </Button>
                                            {e.estado === "generado" && (
                                                <Button size="sm" variant="ghost"
                                                    onClick={() => cerrarEC(e.id)}
                                                    data-testid={`cerrar-periodo-btn`}>
                                                    <Lock className="h-3 w-3 mr-1" /> Cerrar
                                                </Button>
                                            )}
                                            {e.estado === "cerrado" && (
                                                <Button size="sm" variant="ghost"
                                                    onClick={() => reabrirEC(e.id)}
                                                    data-testid={`reabrir-ec-${e.id}`}>
                                                    <Unlock className="h-3 w-3 mr-1" /> Reabrir
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {estados.length === 0 && (
                                    <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Sin estados generados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="catalogo">
                    <div className="rounded-md border border-border bg-card overflow-hidden max-h-[70vh] overflow-y-auto">
                        <table className="trf-table text-xs">
                            <thead className="sticky top-0 bg-card"><tr>
                                <th>Código</th><th>Nombre</th><th>Tipo</th><th>Naturaleza</th>
                            </tr></thead>
                            <tbody>
                                {catalogo.map((c) => (
                                    <tr key={c.codigo}>
                                        <td className="tabular font-medium">{c.codigo}</td>
                                        <td>{c.nombre}</td>
                                        <td><span className="badge-gold text-[10px]">{c.tipo}</span></td>
                                        <td>{c.naturaleza}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
